import React from "react";
import { useQuery } from "../../hooks/useQuery";
import { api } from "../../lib/api";
import { Package, CheckCircle2, Truck, Clock } from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_COLORS = {
  Requested: "status-Commissioned",
  "In Transit": "status-InTransit",
  "Out for Delivery": "status-AtPort",
  Delivered: "status-Delivered",
  Voided: "status-AtCustoms",
};

export default function Dashboard() {
  const { data, loading } = useQuery(() => api.get("/shipments").then((r) => r.data));
  const shipments = data || [];

  const stats = {
    total: shipments.length,
    requested: shipments.filter((s) => s.current_status === "Requested").length,
    transit: shipments.filter((s) => ["In Transit", "Out for Delivery"].includes(s.current_status)).length,
    delivered: shipments.filter((s) => s.current_status === "Delivered").length,
  };

  return (
    <div data-testid="dashboard-page">
      <h1 className="font-serif-bv text-3xl text-[#1B3022]">Operations Dashboard</h1>
      <p className="text-sm text-[#1B3022]/60 mt-1">Real-time view of all HBS manifests.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        {[
          { label: "Total Shipments", value: stats.total, icon: Package, color: "#1B3022" },
          { label: "Requested", value: stats.requested, icon: Clock, color: "#A88420" },
          { label: "In Transit / OFD", value: stats.transit, icon: Truck, color: "#1F3A47" },
          { label: "Delivered", value: stats.delivered, icon: CheckCircle2, color: "#1F3D26" },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white border border-[#1B3022]/15 rounded-sm p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#1B3022]/60">{s.label}</div>
                <Icon size={18} style={{ color: s.color }} />
              </div>
              <div className="font-serif-bv text-4xl text-[#1B3022] tabular mt-3">{s.value}</div>
            </div>
          );
        })}
      </div>

      <div className="mt-10 bg-white border border-[#1B3022]/15 rounded-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#1B3022]/10">
          <h2 className="font-serif-bv text-lg text-[#1B3022]">Recent Manifests</h2>
          <Link to="/admin/shipments" className="text-xs text-[#D4AF37] tracking-widest uppercase">View all →</Link>
        </div>
        <table className="w-full text-sm" data-testid="recent-table">
          <thead className="bg-[#F5EFE0] text-[#1B3022]">
            <tr>
              {["Tracking", "Status", "From → To", "Created"].map((h) => (
                <th key={h} className="px-4 py-2 text-left text-[10px] tracking-widest uppercase font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="px-4 py-8 text-center text-[#1B3022]/60">Loading…</td></tr>}
            {shipments.slice(0, 10).map((s) => (
              <tr key={s.tracking_id} className="border-t border-[#1B3022]/10 hover:bg-[#F5EFE0]/50">
                <td className="px-4 py-2">
                  <Link to={`/admin/shipments`} className="font-mono tracking-widest text-[#1B3022]">{s.tracking_id}</Link>
                </td>
                <td className="px-4 py-2">
                  <span className={`${STATUS_COLORS[s.current_status] || ""} px-2 py-0.5 text-[9px] tracking-widest uppercase rounded-sm`}>{s.current_status}</span>
                </td>
                <td className="px-4 py-2 text-[#1B3022]/85">{s.sender_name} → {s.receiver_name}</td>
                <td className="px-4 py-2 text-[#1B3022]/60">{(s.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
